import { Suspense, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
	getEntityDetailComponent,
	ENTITY_DISPLAY_NAMES,
	ENTITY_ID_PROP_NAMES,
	ENTITY_RESOURCE_PATHS
} from '@/contexts/EntityModalContext';

export function DetachedEntityView() {
	const { entityType, entityId } = useParams<{
		entityType: string;
		entityId: string;
	}>();
	const navigate = useNavigate();

	// Set document title based on entity type
	useEffect(() => {
		const displayName = entityType
			? ENTITY_DISPLAY_NAMES[entityType] || entityType
			: 'Details';
		document.title = `${displayName} Details - Vectro`;

		return () => {
			document.title = 'Vectro';
		};
	}, [entityType]);

	if (!entityType || !entityId) {
		return (
			<div className="flex items-center justify-center min-h-screen text-muted-foreground">
				Invalid entity parameters.
			</div>
		);
	}

	const DetailComponent = getEntityDetailComponent(entityType);

	if (!DetailComponent) {
		return (
			<div className="flex items-center justify-center min-h-screen text-muted-foreground">
				No detail component found for entity type: {entityType}
			</div>
		);
	}

	const idPropName =
		ENTITY_ID_PROP_NAMES[entityType] || `${entityType}Id`;
	const resourcePath = ENTITY_RESOURCE_PATHS[entityType];

	const handleEdit = (id: string) => {
		if (resourcePath) {
			navigate(`${resourcePath}/edit/${id}`);
		}
	};

	const handleClone = (id: string) => {
		if (resourcePath) {
			navigate(`${resourcePath}/clone/${id}`);
		}
	};

	const handleDelete = () => {
		// After deletion, close the detached window
		window.close();
	};

	const props: Record<string, unknown> = {
		[idPropName]: entityId,
		showHeader: true,
		onEdit: handleEdit,
		onClone: handleClone,
		onDelete: handleDelete
	};

	return (
		<div className="min-h-screen bg-white dark:bg-slate-900">
			<div className="max-w-4xl mx-auto p-6">
				<Suspense
					fallback={
						<div className="flex items-center justify-center py-12">
							<Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
							<span className="text-sm text-muted-foreground">
								Loading details...
							</span>
						</div>
					}
				>
					<DetailComponent {...props} />
				</Suspense>
			</div>
		</div>
	);
}
